import React, { useEffect, useState, useCallback } from 'react';
import { PageProps } from '../models/global';
import { Helmet } from 'react-helmet';
import { hideMenu } from '../utils';
import { useLoading } from '../contexts/LoadingContext';
import { useLanguage } from '../LanguageContext';
import { useTranslate } from '../services/translation';
import { getBlogs, PortfolioBlog } from '../api/API';
import BlogList from '../components/BlogList';
import SearchBar from '../components/SearchBar';
import { ProductSkeletonLoader } from '../components/ProductSkeletonLoader';
import { searchTranslationKeys } from '../services/utils';
import translationData from '../translate.json';
import { App } from '../environments/environment';
import '../assets/css/Blog.css';

const { PORTFOLIO_USER_ID, LIMIT_PER_PAGE } = App;

interface BlogsState {
  blogs: PortfolioBlog[];
  total: number;
  hasMore: boolean;
}

const Blog = ({ start }: PageProps) => {
  const { translate } = useTranslate();
  const { language } = useLanguage();
  const { startLoading, stopLoading } = useLoading();
  const [state, setState] = useState<BlogsState>({
    blogs: [],
    total: 0,
    hasMore: false,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const loadBlogs = useCallback(
    async (searchTerm: string = '', skipValue: number = 0) => {
      setIsLoading(true);
      startLoading();

      const response = await getBlogs(
        PORTFOLIO_USER_ID,
        skipValue,
        LIMIT_PER_PAGE,
        searchTerm
      );

      if (response) {
        setState({
          blogs: skipValue === 0 ? response.data.data : [...state.blogs, ...response.data.data],
          total: response.data.count,
          hasMore: (skipValue + LIMIT_PER_PAGE) < response.data.count,
        });
      }

      setIsLoading(false);
      stopLoading();
    },
    [startLoading, stopLoading, state.blogs]
  );

  useEffect(() => {
    start!(2);
    hideMenu();
    loadBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      setSkip(0);
      const translationKeys = searchTranslationKeys(query, language, translationData, 'portfolio.gence.blog');
      const searchTerm = translationKeys || query;
      loadBlogs(searchTerm, 0);
    },
    [loadBlogs, language]
  );

  const handleLoadMore = useCallback(() => {
    const newSkip = skip + LIMIT_PER_PAGE;
    setSkip(newSkip);
    const translationKeys = searchTranslationKeys(searchQuery, language, translationData, 'portfolio.gence.blog');
    const searchTerm = translationKeys || searchQuery;
    loadBlogs(searchTerm, newSkip);
  }, [skip, searchQuery, loadBlogs, language]);

  return (
    <div className="blog-page width-100p">
      <Helmet>
        <meta charSet="utf-8" name="description" content={translate('portfolio.gence.blog_page.desc')} />
        <title>{translate('portfolio.gence.blog_page.title')}</title>
        <link rel="canonical" href={`https://gence.bemanjary.com/${language}/blog`} />
        <meta property="og:title" content={translate('portfolio.gence.blog_page.title')} />
        <meta property="og:description" content={translate('portfolio.gence.blog_page.desc')} />
        <meta property="og:type" content="website" />
        <link rel="alternate" hrefLang="fr" href="https://gence.bemanjary.com/fr/blog" />
        <link rel="alternate" hrefLang="en" href="https://gence.bemanjary.com/en/blog" />
        <link rel="alternate" hrefLang="mg" href="https://gence.bemanjary.com/mg/blog" />
        <link rel="alternate" hrefLang="x-default" href="https://gence.bemanjary.com/fr/blog" />
      </Helmet>

      <div className="blog-page-header">
        <h1 className="blog-page-title white">{translate('common.blog')}</h1>
        <p className="blog-page-subtitle light">
          {state.total} {state.total > 1 ? translate('common.articles') : translate("common.article")}
        </p>
      </div>

      <SearchBar
        onSearch={handleSearch}
        placeholder={translate('common.search_articles')}
      />

      <div className="blog-page-content">
        {isLoading && state.blogs.length === 0 ? (
          <ProductSkeletonLoader count={LIMIT_PER_PAGE} />
        ) : state.blogs.length > 0 ? (
          <>
            <BlogList blogs={state.blogs} />
            {state.hasMore && (
              <div className="blog-load-more">
                <button
                  className="btn-load-more"
                  onClick={handleLoadMore}
                  disabled={isLoading}
                >
                  {isLoading ? translate("common.loading") : translate("common.load_more")}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="blog-page-empty">
            <p className="light">{translate("common.no_blog_found")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;